import { Box, Theme, Typography, SxProps } from '@mui/material';

import { Maybe, TAGContentAlignment } from '@/types';
import {
	HalfScreenButtonCollection,
	HalfScreenBody,
	HalfScreenHeading,
	ContentTypeRichTextContent,
	SmallScreenContent,
	TermsAndConditions as TTermsAndConditions,
	HalfScreenSubHeading,
} from '@/types/generated';
import TermsAndConditions from '@/components/TermsAndConditions';
import ContentTypeRichText from '@/components/ContentTypeRichText';
import ContentfulButton from '@/components/ContentfulButton';
import { resolveEyebrowTextColor } from '@/components/HalfScreen/ContentHalf/ContentHalf.helpers';

import {
	wrapper,
	getBodyTextStyles,
	textContainerStyles,
	halfScreenButtonWrapperStyles,
	buttonStylesTeamtag,
} from './ContentHalf.styles';

interface IContentHalf {
	buttons?: HalfScreenButtonCollection['items'];
	eyebrow?: Maybe<string>;
	eyebrowStyle?: Maybe<string>;
	heading?: Maybe<HalfScreenHeading>;
	subHeading?: Maybe<HalfScreenSubHeading>;
	body?: Maybe<HalfScreenBody>;
	termsAndConditions?: Maybe<TTermsAndConditions>;
	isSmallScreen: boolean;
	isDarkBackground: boolean;
	contentSmallScreen?: Maybe<SmallScreenContent>;
	id: string;
	theme?: Theme;
	containerSx?: SxProps;
	textAlign?: Maybe<TAGContentAlignment>;
}

export default function ContentHalf({
	termsAndConditions,
	eyebrow,
	eyebrowStyle,
	heading,
	subHeading,
	body,
	buttons,
	contentSmallScreen,
	isSmallScreen,
	isDarkBackground,
	id,
	theme,
	containerSx = {},
	textAlign = 'Center',
}: Readonly<IContentHalf>) {
	const headingContent =
		isSmallScreen && contentSmallScreen?.headingSmallScreen ? contentSmallScreen.headingSmallScreen : heading;
	const bodyContent =
		isSmallScreen && contentSmallScreen?.bodySmallScreen ? contentSmallScreen.bodySmallScreen : body;
	const wrapperSx: object = { ...wrapper(textAlign), ...containerSx };
	const textAlignment = (textAlign?.toLowerCase() ? textAlign.toLowerCase() : 'center') as
		| 'left'
		| 'right'
		| 'center';
	return (
		<Box data-testid="section_content_half_screen" id={id} sx={wrapperSx}>
			<Box sx={textContainerStyles(textAlign)}>
				{eyebrow && (
					<Typography
						align={textAlignment}
						data-testid="group_half_screen_eyebrow"
						variant={eyebrowStyle === 'Promo' ? 'bodyLargeBold' : 'bodySmallBold'}
						color={resolveEyebrowTextColor({
							isDarkBackground,
							eyebrowStyle,
						})}
						sx={
							isSmallScreen
								? { alignSelf: 'flex-start', letterSpacing: '3px', color: '#002855' }
								: { letterSpacing: '3px', color: '#002855' }
						}
					>
						{eyebrow}
					</Typography>
				)}

				{headingContent && (
					<ContentTypeRichText
						disableGutters
						disablePaddingOn={['Top', 'Bottom']}
						backgroundColor="transparent"
						dataTestId="group_half_screen_heading"
						centerAlign={textAlign === 'Center'}
						content={headingContent as ContentTypeRichTextContent}
						docProps={{
							margin: 0,
							color: isDarkBackground ? 'text.light' : 'text.primary',
							variant: 'header1',
							fontSize: { xs: '30px', md: '35px' },
							lineHeight: { xs: '40px', md: '48px' },
							textAlign: isSmallScreen && 'left',
						}}
					/>
				)}

				{subHeading && (
					<ContentTypeRichText
						disableGutters
						disablePaddingOn={['Top', 'Bottom']}
						backgroundColor="transparent"
						dataTestId="group_half_screen_subheading"
						centerAlign={textAlign === 'Center'}
						content={subHeading as ContentTypeRichTextContent}
						docProps={{
							margin: '0 0 0.5rem',
							color: 'accent02.main',
							variant: 'header3',
						}}
					/>
				)}

				{bodyContent && (
					<ContentTypeRichText
						disableGutters
						disablePaddingOn={['Top', 'Bottom']}
						dataTestId="group_half_screen_body"
						backgroundColor="transparent"
						centerAlign={textAlign === 'Center'}
						content={bodyContent as ContentTypeRichTextContent}
						docProps={{
							sx: getBodyTextStyles(isDarkBackground, theme),
							fontFamily: 'Messina Sans',
							textAlign: isSmallScreen && 'left',
						}}
					/>
				)}

				{buttons && buttons.length > 0 && (
					<Box data-testid="group_half_screen_buttons_wrapper" sx={halfScreenButtonWrapperStyles(textAlign)}>
						{buttons.map((button) =>
							button ? (
								<ContentfulButton
									key={button.sys.id}
									{...button}
									dataTestId="button_half_screen_cta"
									docProps={{ ...buttonStylesTeamtag }}
								/>
							) : null
						)}
					</Box>
				)}

				{termsAndConditions && (
					<Box data-testid="group_half_screen_terms_and_conditions_wrapper">
						<TermsAndConditions
							{...termsAndConditions}
							textColor={isDarkBackground ? 'text.light' : 'text.secondary'}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
}
